import { useState, useEffect } from 'react';

const VIEWPORT_PREVIEW_IFRAME_TITLE = 'Viewport Preview';

/**
 * Resolves which `window` should evaluate CSS media queries.
 *
 * In the dynamic-experiences dev shell, the experience UI is portaled into
 * `ViewportFrame` (an iframe) while React hooks still run in the parent window.
 * `window.matchMedia` on the parent therefore reflects the monitor width, not
 * the simulated viewport — e.g. a 375px preview still selects desktop layout.
 */
export function resolveMatchMediaWindow(): Window {
  if (typeof window === 'undefined') {
    throw new Error(
      'resolveMatchMediaWindow requires a browser environment with window',
    );
  }

  if (typeof document === 'undefined') {
    return window;
  }

  const previewIframe = document.querySelector(
    `iframe[title="${VIEWPORT_PREVIEW_IFRAME_TITLE}"]`,
  ) as HTMLIFrameElement | null;

  if (previewIframe?.contentWindow) {
    return previewIframe.contentWindow;
  }

  return window;
}

function queryMatches(query: string): boolean {
  return resolveMatchMediaWindow().matchMedia(query).matches;
}

export default function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? queryMatches(query) : false,
  );

  useEffect(() => {
    const targetWindow = resolveMatchMediaWindow();
    const media = targetWindow.matchMedia(query);
    setMatches(media.matches);

    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);

    return () => media.removeEventListener('change', listener);
  }, [query]);

  return matches;
}
